import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, Outlet, Navigate, matchPath } from 'react-router-dom';

import Navbar from './components/layout/Navbar';
import Sidebar from './components/layout/Sidebar';
import Footer from './components/layout/Footer';

import Landing from './pages/Landing';
import Dashboard from './pages/Dashboard';
import Settings from './pages/Settings';
import Pricing from './pages/Pricing';
import Ledger from './pages/Wallet';
import LedgerMonthly from './pages/LedgerMonthly';

import { SupabaseProvider } from './context/SupabaseContext';

import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import LessonsList from './pages/LessonsList';
import StudentsList from './pages/StudentsList';
import NewStudent from './pages/StudentNew';
import StudentProfile from './pages/StudentProfile';
import EditStudent from './pages/StudentEdit';
import BookLesson from './pages/BookLesson';
import LessonFeedback from './pages/LessonFeedback';
import Lesson from './pages/Lesson';
import Onboarding from './pages/Onboarding';
import LessonEditPage from './pages/LessonEdit';
import CashPayment from './pages/CashPayment';
import FormSignupPublic from './pages/FormSignupPublic';
import FormsList from './pages/FormsList';

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  // Check if we're on pages that don't need the main app layout
  const isPublicPage =
  ["/", "/pricing"].includes(location.pathname) ||
    matchPath("/signup-form/:formId", location.pathname) !== null;

  useEffect(() => {
    // Close sidebar when changing routes on mobile
    setSidebarOpen(false);
  }, [location.pathname]);

  const ProtectedRoute = () => {
    const { session, loading } = useAuth();

    if (loading) return null;
    return session ? <Outlet /> : <Navigate to="/" replace />;
  };

  return (
    <ThemeProvider>
      <AuthProvider>
        {isPublicPage ? (
          <Routes>
            <Route path="/signup-form/:formId" element={<FormSignupPublic />} />
            <Route path="/" element={<Landing />} />
            <Route path="/pricing" element={<Pricing />} />
          </Routes>
        ) : (
          <SupabaseProvider>
              <div className="flex h-screen overflow-hidden">
                <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />

                <div className="flex flex-col flex-1 h-screen overflow-y-auto">
                  <Navbar onMenuClick={() => setSidebarOpen(!sidebarOpen)} />

                  <main className="flex-1 p-2 px-3 md:p-6 pb-24 bg-transparent transition-colors duration-300">
                    <Routes>

                      {/* only for authed users */}
                      <Route element={<ProtectedRoute />}>

                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/ledger" element={<Ledger />} />
                        <Route path="/alltransactions" element={<LedgerMonthly />} />
                        <Route path="/pay-cash/:studentId" element={<CashPayment />} />
                        <Route path="/forms" element={<FormsList />} />
                        
                        {/* STUDENTS */}
                        <Route path="/students" element={<StudentsList account_type='student' />} />
                        <Route path="/students/new" element={<NewStudent account_type='student' />} />
                        <Route path="/students/:id" element={<StudentProfile account_type='student' />} />
                        <Route path="/students/:id/edit" element={<EditStudent account_type='student' />} />
                        <Route path="/students/:id/ledger" element={<Ledger />} />

                        {/* TEACHERS */}
                        <Route path="/teachers" element={<StudentsList account_type='teacher' />} />
                        <Route path="/teachers/new" element={<NewStudent account_type='teacher' />} />
                        <Route path="/teachers/:id" element={<StudentProfile account_type='teacher' />} />
                        <Route path="/teachers/:id/edit" element={<EditStudent account_type='teacher' />} />
                        <Route path="/teachers/:id/ledger" element={<Ledger />} />

                        {/* PARENTS */}
                        <Route path="/parents" element={<StudentsList account_type='parent' />} />
                        <Route path="/parents/new" element={<NewStudent account_type='parent' />} />
                        <Route path="/parents/:id" element={<StudentProfile account_type='parent' />} />
                        <Route path="/parents/:id/edit" element={<EditStudent account_type='parent' />} />

                        <Route path="/settings" element={<Settings />} />

                        {/* LESSONS */}
                        <Route path="/lesson" element={<LessonsList />} />
                        <Route path="/lesson/create" element={<BookLesson />} />
                        <Route path="/lesson/:lessonId/feedback" element={<LessonFeedback />} />
                        <Route path="/lesson/:lessonId" element={<Lesson />} />
                        <Route path="/lesson/:lessonId/edit" element={<LessonEditPage />} />

                        <Route path="/auth/callback" element={<Onboarding />} />
                        <Route path="/onboarding" element={<Onboarding />} />
                      </Route>

                    </Routes>
                  </main>

                  <Footer />
                </div>
              </div>

          </SupabaseProvider>
        )}
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
